/**
 * workOrderSampleUploadService.ts — API calls for the WO "Upload samples" door
 * (2026-09-10). Three steps: analyze a workbook (xlsx/xlsm — CSV is parsed in
 * the browser by woUploadParser), reconcile the normalized rows against the
 * work order (dry run), apply.
 */
import apiClient from './apiClient';
import type {
  WoSampleUploadApplyResult,
  WoSampleUploadOptions,
  WoSampleUploadReconcileResult,
  WoSampleUploadRowInput,
  WoSampleWorkbookAnalysis,
} from '../types/workOrderSampleUpload';

export const SCHEDULE_IMPACT_UNACKNOWLEDGED = 'SCHEDULE_IMPACT_UNACKNOWLEDGED';

/** Parse a MoftakiLab intake workbook or legacy KeyLAB form WITHOUT saving. */
export const analyzeWorkOrderSampleWorkbook = async (file: File): Promise<WoSampleWorkbookAnalysis> => {
  const form = new FormData();
  form.append('file', file);
  const response = await apiClient.post<WoSampleWorkbookAnalysis>(
    '/workorders/sample-upload/analyze',
    form,
    // Override the client's JSON default: axios 1.x would otherwise serialize
    // the FormData as JSON. The browser still sets the multipart boundary.
    { headers: { 'Content-Type': 'multipart/form-data' } },
  );
  return response.data;
};

/** Dry run: how the rows land on the work order (fills / creates / expected vs actual). */
export const reconcileWorkOrderSampleUpload = async (
  workOrderKey: string,
  rows: WoSampleUploadRowInput[],
  opts: WoSampleUploadOptions = {},
): Promise<WoSampleUploadReconcileResult> => {
  const response = await apiClient.post<WoSampleUploadReconcileResult>(
    `/workorders/${workOrderKey}/sample-upload/reconcile`,
    { rows, ...opts },
  );
  return response.data;
};

/** Write. 409 SCHEDULE_IMPACT_UNACKNOWLEDGED when the plan drifts and was not confirmed. */
export const applyWorkOrderSampleUpload = async (
  workOrderKey: string,
  rows: WoSampleUploadRowInput[],
  opts: WoSampleUploadOptions,
): Promise<WoSampleUploadApplyResult> => {
  const response = await apiClient.post<WoSampleUploadApplyResult>(
    `/workorders/${workOrderKey}/sample-upload/apply`,
    { rows, ...opts },
  );
  return response.data;
};
